import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { TrendingUp, Users, ArrowRight, Layers, Calculator, DollarSign } from "lucide-react";

const PERSONAL = 1000;                        // your own personal sale
const OVERRIDE = [200, 100, 75, 50, 50, 25];  // overrides on downline levels 1..6

const LEVELS = [
  { level: "Level 1", amount: 200, note: "Your first level (direct recruits)" },
  { level: "Level 2", amount: 100, note: "Second level" },
  { level: "Level 3", amount: 75, note: "Third level" },
  { level: "Level 4", amount: 50, note: "Fourth level" },
  { level: "Level 5", amount: 50, note: "Fifth level" },
  { level: "Level 6", amount: 25, note: "Sixth level" },
];

const money = (n) => `$${Math.round(n).toLocaleString()}`;

export default function Compensation() {
  const navigate = useNavigate();
  const [width, setWidth] = useState(5);      // reps each rep recruits ("5 get 5")
  const [sales, setSales] = useState(1);       // physician sales per rep per month

  const personalMonthly = useMemo(() => 1 * sales * PERSONAL, [sales]);

  const rows = useMemo(() => {
    return OVERRIDE.map((rate, i) => {
      const level = i + 1;
      const reps = Math.pow(width, level); // Level 1 = your first level = width^1
      const monthly = reps * sales * rate;
      return { level, reps, rate, monthly };
    });
  }, [width, sales]);

  const monthlyTotal = personalMonthly + rows.reduce((a, r) => a + r.monthly, 0);
  const teamSize = rows.reduce((a, r) => a + r.reps, 0);

  return (
    <div data-testid="compensation-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Back Office</p>
        <h1 className="font-display text-3xl text-navy mt-1">Compensation Plan</h1>
      </div>

      {/* PERSONAL SALES CALLOUT */}
      <div className="bg-patriot text-white rounded-xl p-6 sm:p-8 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="personal-sales-callout">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-xl bg-white/15 flex items-center justify-center"><DollarSign className="h-7 w-7" /></div>
          <div>
            <p className="text-white/80 text-sm uppercase tracking-wide">Personal Sales</p>
            <h2 className="font-display text-2xl sm:text-3xl">Your own sale earns you $1,000</h2>
          </div>
        </div>
        <span className="font-display text-4xl sm:text-5xl font-black">$1,000</span>
      </div>

      <p className="text-muted-foreground mb-8 max-w-2xl">
        On top of your personal sales, build your team and earn overrides across six levels.
        Your first level (direct recruits) earns you <span className="font-semibold text-navy">$200</span> per
        physician sale, paying down through six levels of your organization.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-border rounded-xl p-6">
          <div className="h-11 w-11 rounded-lg bg-navy/5 flex items-center justify-center mb-4"><Users className="h-5 w-5 text-navy" /></div>
          <h3 className="font-display text-lg text-navy mb-1">Build Your Team</h3>
          <p className="text-sm text-muted-foreground">Recruit representatives and grow a six-level referral network.</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <div className="h-11 w-11 rounded-lg bg-navy/5 flex items-center justify-center mb-4"><Layers className="h-5 w-5 text-navy" /></div>
          <h3 className="font-display text-lg text-navy mb-1">6-Level Overrides</h3>
          <p className="text-sm text-muted-foreground">Earn overrides on every physician sale across six levels of your team.</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-6">
          <div className="h-11 w-11 rounded-lg bg-patriot/10 flex items-center justify-center mb-4"><TrendingUp className="h-5 w-5 text-patriot" /></div>
          <h3 className="font-display text-lg text-navy mb-1">Compounding Income</h3>
          <p className="text-sm text-muted-foreground">Overrides compound as your downline builds their own teams.</p>
        </div>
      </div>

      {/* OVERRIDE SCHEDULE */}
      <div className="bg-navy-deep text-white rounded-xl p-8 afha-grain mb-8">
        <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot-light mb-6">Override Schedule</p>
        <div className="grid gap-3 max-w-2xl">
          {LEVELS.map((l, i) => (
            <div
              key={l.level}
              className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-6 py-4 hover:bg-white/10 transition-colors"
              style={{ marginLeft: `${i * 10}px` }}
              data-testid={`comp-level-${i + 1}`}
            >
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm text-patriot-light w-16">{l.level}</span>
                <span className="text-sm text-white/60">{l.note}</span>
              </div>
              <span className="font-display text-2xl font-bold">${l.amount}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-white/20 mt-2 pt-4 px-6">
            <span className="font-semibold">Total override across 6 levels</span>
            <span className="font-display text-3xl font-black text-patriot-light">$500</span>
          </div>
        </div>
      </div>

      {/* OVERRIDE CALCULATOR */}
      <div className="bg-white border border-border rounded-xl p-8 mb-8" data-testid="override-calculator">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-lg bg-navy/5 flex items-center justify-center"><Calculator className="h-5 w-5 text-navy" /></div>
          <div>
            <h2 className="font-display text-2xl text-navy">Override Calculator</h2>
            <p className="text-sm text-muted-foreground">Here is an example of earnings if you build a good team.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl">
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-navy">Reps each rep recruits</label>
              <span className="font-display text-2xl text-patriot font-bold" data-testid="calc-width-value">{width}</span>
            </div>
            <Slider min={1} max={10} step={1} value={[width]} onValueChange={(v) => setWidth(v[0])} data-testid="calc-width-slider" />
            <p className="text-xs text-muted-foreground mt-2">"Use 5, get 5" — each rep sponsors {width} reps.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-navy">Physician sales / rep / month</label>
              <span className="font-display text-2xl text-patriot font-bold" data-testid="calc-sales-value">{sales}</span>
            </div>
            <Slider min={1} max={5} step={1} value={[sales]} onValueChange={(v) => setSales(v[0])} data-testid="calc-sales-slider" />
            <p className="text-xs text-muted-foreground mt-2">Physicians each rep onboards per month.</p>
          </div>
        </div>

        <div className="mt-8 rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="text-navy font-semibold">Level</TableHead>
                <TableHead className="text-navy font-semibold text-right">Reps</TableHead>
                <TableHead className="text-navy font-semibold text-right">Comp / sale</TableHead>
                <TableHead className="text-navy font-semibold text-right">Monthly earnings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-patriot/5" data-testid="calc-row-personal">
                <TableCell className="font-semibold text-patriot">Personal (You)</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">1</TableCell>
                <TableCell className="text-right text-muted-foreground">$1,000</TableCell>
                <TableCell className="text-right font-semibold text-navy">{money(personalMonthly)}</TableCell>
              </TableRow>
              {rows.map((r) => (
                <TableRow key={r.level} data-testid={`calc-row-${r.level}`}>
                  <TableCell className="font-medium text-navy">
                    {r.level === 1 ? "Level 1 (First level)" : `Level ${r.level}`}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{r.reps.toLocaleString()}</TableCell>
                  <TableCell className="text-right text-muted-foreground">${r.rate}</TableCell>
                  <TableCell className="text-right font-semibold text-navy">{money(r.monthly)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-muted/40 border border-border rounded-lg p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total team</p>
            <p className="font-display text-3xl text-navy mt-1" data-testid="calc-team-size">{teamSize.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">reps across 6 levels</p>
          </div>
          <div className="bg-muted/40 border border-border rounded-lg p-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Monthly income</p>
            <p className="font-display text-3xl text-navy mt-1" data-testid="calc-monthly">{money(monthlyTotal)}</p>
            <p className="text-xs text-muted-foreground">per month</p>
          </div>
          <div className="bg-patriot text-white rounded-lg p-5">
            <p className="text-xs uppercase tracking-wide text-white/80">Annual income</p>
            <p className="font-display text-3xl mt-1" data-testid="calc-annual">{money(monthlyTotal * 12)}</p>
            <p className="text-xs text-white/80">per year</p>
          </div>
        </div>

        <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
          This is a hypothetical illustration based on the assumptions you set above, not a
          guarantee of income. Actual results depend on your effort, your team's activity,
          and market conditions.
        </p>
      </div>

      <div className="bg-white border border-border rounded-xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="font-display text-2xl text-navy">Grow your organization</h2>
          <p className="text-muted-foreground mt-1">Share your referral link to recruit new representatives.</p>
        </div>
        <Button onClick={() => navigate("/dashboard/share")} data-testid="comp-share-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2 shrink-0">
          Share &amp; Grow <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
